import APIError from '@rover/react-lib/src/apis/APIError';
import fetchInternalLinkingResponseFactory from '@rover/react-lib/src/factories/fetchInternalLinkingResponseFactory';
import type {
  ActionCreator,
  Duck,
  DuckOptions,
  EffectCreator,
  SelectorCreator,
} from '@rover/react-lib/src/lib/redux-duck';
import { createDuck } from '@rover/react-lib/src/lib/redux-duck';
import generateActions from '@rover/react-lib/src/redux/ducks/generateActions';
import { Anchor } from '@rover/types/src/Anchor';
import type { FetchInternalLinkingResponse } from '@rover/types/src/apiResponses/FetchInternalLinkingResponse';
import type { Locality } from '@rover/types/src/Locality';
import { captureError } from '@rover/rsdk/src/modules/ErrorReporting';
import getPath from '@rover/shared/js/utils/getPath';
import { Http404Error } from '@rover/utilities/HttpError';
import { pathToLocality } from '@rover/utilities/locality';

import { get } from './internalLinking.api';

export type State = {
  loading: boolean;
  errorMessage: string;
  fetchInternalLinkingResponse: FetchInternalLinkingResponse;
};

type Actions = {
  fetchInternalLinkingPending: ActionCreator<void>;
  fetchInternalLinkingFailure: ActionCreator<string>;
  fetchInternalLinkingSuccess: ActionCreator<FetchInternalLinkingResponse>;
};

type Effects = {
  fetchInternalLinking: EffectCreator<Locality>;
};

type Selectors = {
  getInternalLinking: SelectorCreator<FetchInternalLinkingResponse>;
  getBreadcrumbs: SelectorCreator<Anchor[]>;
  getCityOrNeighborhoodServices: SelectorCreator<Anchor[]>;
  getCitiesOrNeighborhoods: SelectorCreator<Anchor[]>;
};

type Options = DuckOptions<State, Actions, Effects, Selectors>;

export const options: Options = {
  name: 'internalLinking',
  initialState: {
    loading: false,
    errorMessage: '',
    fetchInternalLinkingResponse: fetchInternalLinkingResponseFactory(),
  },
  actions: {
    ...generateActions('fetchInternalLinking'),
  },
  effects: {
    fetchInternalLinking:
      () =>
      async (
        dispatch,
        getState,
        { fetchInternalLinkingPending, fetchInternalLinkingSuccess, fetchInternalLinkingFailure }
      ) => {
        dispatch(fetchInternalLinkingPending());

        try {
          const path = `${getPath()}/`;
          const locality = pathToLocality(path);
          const fetchInternalLinkingResponse = await get(locality);
          dispatch(fetchInternalLinkingSuccess(fetchInternalLinkingResponse));
        } catch (e) {
          if (e instanceof Http404Error || (e instanceof APIError && e.status === 404)) {
            throw new Http404Error();
          } else if (e instanceof Error) {
            dispatch(fetchInternalLinkingFailure(e.message));
            captureError(e);
          }
        }
      },
  },
  selectors: {
    getInternalLinking: (getState, createSelector) =>
      createSelector([getState], (x) => x.fetchInternalLinkingResponse),
    getBreadcrumbs: (getState, createSelector, { getInternalLinking: internalLinking }) =>
      createSelector([internalLinking], (x) => x.results.breadcrumbs),
    getCityOrNeighborhoodServices: (
      getState,
      createSelector,
      { getInternalLinking: internalLinking }
    ) => createSelector([internalLinking], (x) => x.results.cityOrNeighborhoodServices),
    getCitiesOrNeighborhoods: (getState, createSelector, { getInternalLinking: internalLinking }) =>
      createSelector([internalLinking], (x) => x.results.citiesOrNeighborhoods),
  },
};

export const { actions, selectors, effects, reducer } = createDuck(options) as Duck<
  State,
  Actions,
  Effects,
  Selectors
>;
